import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { X, ArrowRight, Calendar, Person, Tag, FileText, Building } from 'react-bootstrap-icons';
import { formatCurrency } from '../../../../../../../../../utils/functions';
import TableDateFormatter from '../../../../../../../../../components/common/Table/TableDateFormatter';
import './MovementDetailsModal.scss';

interface CajaMovement {
  caja_movement_id: number;
  workstation_id: number;
  workstation_name: string;
  user_id: number;
  user_name: string;
  before_amount: number;
  after_amount: number;
  amount: number;
  description: string | null;
  is_positive: boolean;
  caja_movement_type_id: number;
  caja_movement_type_name: string;
  caja_movement_metadata: any;
  timestamp: string;
  local_timestamp: string;
}

interface MovementDetailsModalProps {
  show: boolean;
  onClose: () => void;
  movement: CajaMovement;
}

const MovementDetailsModal: React.FC<MovementDetailsModalProps> = ({
  show,
  onClose,
  movement
}) => {
  const isValidTimestamp = (timestamp: string | null | undefined): boolean => {
    if (!timestamp || timestamp === '') return false;
    const date = new Date(timestamp);
    return !isNaN(date.getTime());
  };

  return (
    <Modal 
      show={show} 
      onHide={onClose} 
      centered 
      className="movement-details-modal"
      // Se eliminó size="lg" para compactar via CSS
    >
      <Modal.Header className="movement-modal-header">
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">
            Detalles del Movimiento
          </Modal.Title>
        </div>
        <Button variant="none" className="modal-close-button" onClick={onClose}>
          <X size={20} />
        </Button>
      </Modal.Header>

      <Modal.Body className="movement-modal-body">
        {/* Compact Header with Movement Info */}
        <div className="movement-header-section">
          <div className="movement-id-chip">
            <Tag size={12} />
            <span>#{movement.caja_movement_id}</span>
          </div>
          <div className={`movement-type-chip ${movement.is_positive ? 'positive' : 'negative'}`}>
            {movement.caja_movement_type_name}
          </div>
        </div>

        {/* Unified Flow: Amounts + User + Caja */}
        <div className="movement-unified-section">
          <div className="amounts-flow">
            <div className="amount-item">
              <span className="amount-label">Antes</span>
              <span className="amount-value">{formatCurrency(movement.before_amount)}</span>
            </div>
            
            <div className="flow-indicator">
              <ArrowRight size={16} className="flow-arrow" />
              <div className={`change-badge ${movement.is_positive ? 'positive' : 'negative'}`}>
                {movement.is_positive ? '+' : ''}{formatCurrency(movement.amount)}
              </div>
            </div>
            
            <div className="amount-item">
              <span className="amount-label">Después</span>
              <span className="amount-value">{formatCurrency(movement.after_amount)}</span>
            </div>
          </div>

          <div className="inline-details">
            <div className="inline-detail-item">
              <Person size={12} />
              <span className="inline-detail-value">{movement.user_name}</span>
            </div>
            <div className="inline-detail-item">
              <Building size={12} />
              <span className="inline-detail-value">{movement.workstation_name}</span>
            </div>
          </div>
        </div>

        {/* Date row */}
        <div className="date-row">
          <Calendar size={13} />
          <span className="date-label">Fecha:</span>
          <span className="date-value">
            {isValidTimestamp(movement.local_timestamp) ? (
              <TableDateFormatter
                data={movement.local_timestamp}
                isRealTime={false}
                stopCounting
                isTimestamp
              />
            ) : (
              <span className="text-muted">No disponible</span>
            )}
          </span>
        </div>

        {/* Description if available */}
        {movement.description?.trim() && (
          <div className="movement-description-compact">
            <div className="description-header">
              <FileText size={14} />
              <span>Descripción</span>
            </div>
            <p className="description-text">{movement.description.trim()}</p>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default MovementDetailsModal;